import { z } from 'zod';
import { IsoTime } from './basics.js';
/*
 * What is on a server, as its Wazuh agent's syscollector found it and the
 * central API read it from the manager (build plan P11.8.7): the operating
 * system, hardware and addresses, the installed packages, the listening ports
 * and the running processes. Shapes checked against what 4.14.8 answers
 * (`packages/api/test/fixtures/wazuh-api/4.14.8/syscollector-*.json`). The
 * manager's text, read by the server's root-run agent; bounded all the same.
 *
 * A process's arguments are never read: a command line can carry a password
 * (rule 5). Its executable is enough to say what it is.
 */
export const INVENTORY_LIMITS = {
    packages: 20_000,
    ports: 2_000,
    processes: 5_000,
    addresses: 200,
    text: 255,
    command: 1000,
};
/** Where a listening socket can be reached from, judged by the server's own addresses. */
export const PortReach = z.enum(['loopback', 'private', 'public']);
export const PortProtocol = z.enum(['tcp', 'tcp6', 'udp', 'udp6']);
/**
 * A database port against `db_exposure`'s word on it (P11.8.7's cross-check):
 * `reported` — `db_exposure` has said it in the last day; `not_reported` — it is
 * on here and has not; `off` — `db_exposure` is not on for this server. Only a
 * public database port is checked; null otherwise.
 */
export const DbExposureCheck = z.enum(['reported', 'not_reported', 'off']);
const Text = z.string().min(1).max(INVENTORY_LIMITS.text);
export const InventoryAddress = z.object({
    iface: z.string().max(64),
    address: z.string().min(1).max(64),
    family: z.enum(['ipv4', 'ipv6']),
});
export const InventorySystem = z.object({
    hostname: Text.nullable(),
    /** `Ubuntu`, and its version as the system names it: `24.04.5 LTS (Noble Numbat)`. */
    osName: Text.nullable(),
    osVersion: Text.nullable(),
    osCodename: Text.nullable(),
    /** `ubuntu`, `debian`: what advisories are matched against (P11.8.8). */
    osPlatform: Text.nullable(),
    kernelRelease: Text.nullable(),
    architecture: Text.nullable(),
    cpuName: Text.nullable(),
    cpuCores: z.number().int().nonnegative().nullable(),
    ramTotalBytes: z.number().int().nonnegative().nullable(),
    addresses: z.array(InventoryAddress).max(INVENTORY_LIMITS.addresses),
});
export const InventoryPort = z.object({
    protocol: PortProtocol,
    address: z.string().min(1).max(64),
    port: z.number().int().min(0).max(65535),
    /** Null when the agent could not tell (a socket in another network namespace). */
    pid: z.number().int().positive().nullable(),
    /** The process's own name (a thread's, for some: Node says `MainThread`). */
    process: Text.nullable(),
    /** Its executable, from the process list, where the pid is in it. */
    command: z.string().max(INVENTORY_LIMITS.command).nullable(),
    reach: PortReach,
    firstSeenAt: IsoTime,
    /** What normally listens there, for a database port: `PostgreSQL`. */
    database: Text.nullable(),
    dbExposure: DbExposureCheck.nullable(),
});
export const InventoryProcess = z.object({
    pid: z.number().int().nonnegative(),
    ppid: z.number().int().nonnegative().nullable(),
    name: Text,
    user: Text.nullable(),
    /** The executable, never its arguments. */
    command: z.string().max(INVENTORY_LIMITS.command).nullable(),
    rssBytes: z.number().int().nonnegative().nullable(),
    startedAt: IsoTime.nullable(),
});
export const InventoryPackage = z.object({
    name: Text,
    version: Text,
    architecture: z.string().max(64),
    /** `deb`, `rpm`, `pypi`, `npm`… as syscollector names the source. */
    format: z.string().max(32),
    /** The source package where it differs from the name (Debian's `Source:`). */
    source: Text.nullable(),
    vendor: Text.nullable(),
    firstSeenAt: IsoTime,
});
/** `GET /v1/servers/:id/inventory`: everything but the packages, which page. */
export const ServerInventory = z.object({
    /** Null before the API has read this server. */
    system: InventorySystem.nullable(),
    /** Public first, then private, then loopback; by port within each. */
    ports: z.array(InventoryPort).max(INVENTORY_LIMITS.ports),
    /** Largest memory first. */
    processes: z.array(InventoryProcess).max(INVENTORY_LIMITS.processes),
    packages: z.object({
        total: z.number().int().nonnegative(),
        /** Packages first seen within the last day, where this is not the first read. */
        newToday: z.number().int().nonnegative(),
    }),
    /** The newest change syscollector made to each kind, as the manager holds it. */
    scannedAt: z.object({
        system: IsoTime.nullable(),
        packages: IsoTime.nullable(),
        ports: IsoTime.nullable(),
        processes: IsoTime.nullable(),
    }),
    /** When the API last read this server's inventory; null where it never has. */
    readAt: IsoTime.nullable(),
});
export const INVENTORY_PACKAGE_PAGE_DEFAULT = 100;
export const INVENTORY_PACKAGE_PAGE_MAX = 500;
/** `GET /v1/servers/:id/inventory/packages`: by name, `q` a name or source it contains. */
export const InventoryPackageQuery = z.object({
    q: z
        .string()
        .trim()
        .max(100)
        .regex(/^[A-Za-z0-9+._:~-]*$/, 'letters, digits and + . _ : ~ - only')
        .optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(INVENTORY_PACKAGE_PAGE_MAX)
        .default(INVENTORY_PACKAGE_PAGE_DEFAULT),
    cursor: z.string().max(1000).optional(),
});
/**
 * Debian's `Source:` as dpkg gives it, `util-linux (2.39.3-9ubuntu6.6)` where the
 * source's version differs: the name only. Blank (syscollector's `" "`) is none.
 */
export function sourcePackageName(source) {
    const name = (source ?? '').trim().replace(/\s*\(.*\)$/, '');
    return name === '' ? null : name.slice(0, INVENTORY_LIMITS.text);
}
/**
 * The source package's version where dpkg gives one apart (`util-linux
 * (2.39.3-9ubuntu6.6)` for `bsdutils 1:2.39.3-9ubuntu6.6`): what an advisory's
 * fixed version is compared with (P11.8.8). Null where it is the package's own.
 */
export function sourcePackageVersion(source) {
    const version = /\(([^()\s]+)\)\s*$/.exec((source ?? '').trim())?.[1];
    return version === undefined ? null : version.slice(0, INVENTORY_LIMITS.text);
}
//# sourceMappingURL=inventory.js.map